import React, { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") === "compass" ? "/compass" : null;
  const returnToClients = searchParams.get("next") === "clients";

  if (user && user !== false) return <Navigate to={(user.role === "crm_staff" || (returnToClients && user.role === "admin") ? "/clients" : null) || nextPath || (user.has_access ? "/dashboard" : user.has_compass ? "/compass" : "/")} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F7F9FC] text-[#17324D]">
      <div className="hidden lg:block relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/auth-hero.png)`, backgroundColor: "#22476B", backgroundBlendMode: "luminosity" }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(23,50,77,0.15), rgba(15,35,56,0.78))" }} />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.3em] opacity-80">{nextPath ? "Alaine's Compass™" : "Alaine's Bench™"}</div>
          <h1 className="text-5xl font-bold mt-2 leading-tight">
            {nextPath ? "Return to your guided founder workspace." : "Nutrition labels built on FDA labeling rules — in minutes."}
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            {nextPath ? "Continue your roadmap, templates, resources, and digital guides." : "Precision rounding per 21 CFR 101.9 · 9-allergen FALCPA + FASTER coverage · Curated herb, spice, yeast & filler database."}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#17324D] text-white grid place-items-center font-bold">A</div>
              <div className="font-bold text-lg">{nextPath ? "Alaine's Compass" : "Alaine's Bench"}<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
            </div>
            <h2 className="text-3xl font-bold mt-6 tracking-tight">Welcome back</h2>
            <p className="text-stone-500 text-sm mt-1">{returnToClients ? "Sign in with your employee or administrator account to open Clients & Pipeline." : nextPath ? "Sign in to your guided workspace" : "Sign in to your compliance studio"}</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 border-[#C5D3E2] focus-visible:ring-[#4F6F92]" data-testid="login-email-input"/>
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 border-[#C5D3E2] focus-visible:ring-[#4F6F92]" data-testid="login-password-input"/>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full bg-[#17324D] hover:bg-[#22476B] text-white" disabled={loading}
            data-testid="login-submit-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          {nextPath && (
            <div className="text-sm text-stone-500 text-center">
              New to Compass? <Link to="/register?next=compass" className="text-[#22476B] font-medium hover:underline">Create an account</Link>
            </div>
          )}
          {nextPath ? (
            <div className="text-[11px] text-stone-400 text-center border-t pt-3 leading-relaxed">
              Compass is a founder guided workspace. Consulting is separate, and Alaine's Bench remains internal-only.
            </div>
          ) : (
            <div className="text-[11px] text-stone-400 text-center border-t pt-3 leading-relaxed">
              Alaine's Bench is the internal platform of Alaine &amp; Co. — accounts are
              provisioned for consulting clients. Visit{" "}
              <a href="https://alaineandco.com/" className="text-[#22476B] hover:underline">alaineandco.com</a>{" "}
              to work with us.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
